import {RoomEngineErrorCode} from "./room-engine/error";
import {TinyEmitter} from "tiny-emitter";
import {StreamType} from "./api/room-models";
import {Content} from "./api/content-api";
import errorToJSON from 'error-to-json';
import $d from "./device";
import Emojis from "./emojis";
import escape from 'lodash/escape';
import gwm from "gwm";

/**
 * 生成一个随机数 (含最大值，含最小值)
 *
 * @param min
 * @param max
 * @returns {number}
 */
export function makeRandomInt(min: number, max: number): number {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export async function sleep(time: number): Promise<ReturnType<typeof setTimeout>> {
    return new Promise(resolve => setTimeout(resolve, time));
}

export function duration(time: number): string {
    time = time / 1000;

    const hours = Math.floor(time/3600);
    const minutes = Math.floor((time%3600)/60);
    const seconds = Math.floor(time%60);

    let duration = ("" + seconds).padStart(2, "0");
    if (hours === 0) {
        duration = "" + minutes + ":" + duration;
    } else {
        duration = ("" + minutes).padStart(2, "0") + ":" + duration;
    }

    if (hours > 0) {
        duration = "" + hours + ":" + duration;
    }

    return duration;
}

/**
 * 00:00:00
 * @param time 毫秒
 */
export function dateStringH(time: number): string {
    const mss = time;
    if (mss === 0) {
        return "00:00:00";
    }

    const hours = Math.floor(mss / (1000 * 60 * 60));
    const minutes = Math.floor((mss % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((mss % (1000 * 60)) / 1000);

    let h: string;
    let m: string;
    let s: string;
    if (hours < 10) {
        h = "0" + hours;
    } else {
        h = String(hours);
    }
    if (minutes < 10) {
        m = "0" + minutes;
    } else {
        m = String(minutes);
    }
    if (seconds < 10) {
        s = "0" + seconds;
    } else {
        s = String(seconds);
    }
    return h + ":" + m + ":" + s;
}

/**
 * 00:00:00/00:00
 * @param time 毫秒
 */
export function dateString(time: number): string {
    const mss = time;
    if (mss === 0) {
        return "00:00";
    }

    const hours = Math.floor(mss / (1000 * 60 * 60));
    const minutes = Math.floor((mss % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((mss % (1000 * 60)) / 1000);

    let h: string;
    let m: string;
    let s: string;
    if (hours < 10) {
        h = "0" + hours;
    } else {
        h = String(hours);
    }
    if (minutes < 10) {
        m = "0" + minutes;
    } else {
        m = String(minutes);
    }
    if (seconds < 10) {
        s = "0" + seconds;
    } else {
        s = String(seconds);
    }

    if (h === "00") {
        return m + ":" + s;
    }
    return h + ":" + m + ":" + s;
}

/**
 *
 * Animate.CSS 辅助函数
 *
 * @param el 需要加动效的 DOM 元素
 * @param animation 动画名称
 * @param speed 动画执行速度
 */
export function animateCSS(el: HTMLElement, animation: string, speed: "normal" | "slow" | "slower" | "fast" | "faster" = "normal"): Promise<void> {
    return new Promise<void>((resolve) => {
        if (!el.classList) {
            return;
        }
        const classes: string[] = [];
        classes.push('animate__animated');
        classes.push(`animate__${animation}`);
        if (speed !== 'normal') {
            classes.push(`animate__${speed}`);
        }

        el.classList.add(...classes);

        function handleAnimationEnd() {
            el.classList.remove(...classes);
            resolve();
        }

        el.addEventListener('animationend', handleAnimationEnd, {once: true});
    });
}


export interface FatalErrorEvent {
    title: string;
    code: number;
    message: string;
}

// eslint-disable-next-line
export function processEngineError(title: string, error: any, $bus: TinyEmitter) {
    if (error.code && error.code === RoomEngineErrorCode.ROOM_IS_FINISHED) {
        $bus.emit('room-finish');
    } else if (error.code && error.code == RoomEngineErrorCode.USER_IS_BLOCKED) {
        $bus.emit('message:block');
    } else {
        $bus.emit('fatal-error', {
            title: title,
            code: error.code ? error.code : 'UNKNOWN',
            message: error.message,
        } as FatalErrorEvent);
    }
}

/**
 * 复制到剪切板
 * @param message
 */
export function copyToClip(message: string): void {
    const input = document.createElement('input');
    document.body.appendChild(input);
    input.setAttribute('value', message);
    input.select();
    if (document.execCommand('copy')) {
        document.execCommand('copy');
    }
    document.body.removeChild(input);
}

/**
 * 解析json文件数据
 * @param data
 */
// eslint-disable-next-line
export function getJsonFileData(data: any) {
    // eslint-disable-next-line
    let objects: Array<any>;
    if (data instanceof Object) {
        objects = [data];
    } else {
        const jsonLines = data.split(/\n/);
        if (!jsonLines[jsonLines.length - 1]) {
            jsonLines.pop();
        }
        const jsonString = "[" + jsonLines.join(",") + "]";
        objects = JSON.parse(jsonString);
    }
    return objects;
}

/**
 * 保存观看时间
 * @param roomId
 * @param value 毫秒
 */
export function saveWatchTime(roomId: number, value: number): void {
    if (roomId === 0 || value < 5000) {
        return;
    }
    const time = Math.floor(value / 1000);
    localStorage.setItem("watchTime-" + roomId, time + "");
}

/**
 * 获取观看时间
 * @param roomId
 * @return 秒
 */
export function getWatchTime(roomId: number): number {
    // eslint-disable-next-line
    const time: any = localStorage.getItem("watchTime-" + roomId);
    if (time) {
        return parseInt(time);
    }
    return 0;
}

export function saveStreamPlayerType(type: string): void {
    localStorage.setItem("forceStreamPlayer2", type);
}

export function getStreamPlayerType(): string | null {
    return localStorage.getItem("forceStreamPlayer2");
}

export function setBackgroundPlay(): boolean {
    const enable = !getBackgroundPlay();
    localStorage.setItem("backgroundPlay", enable ? "on" : "off");
    return enable;
}

export function getBackgroundPlay(): boolean {
    const enable = localStorage.getItem("backgroundPlay");
    return "on" === enable;
}

export interface NetQualityLevel {
    level: number

    name: string

    desc: string
}

/**
 * 是https
 */
export function checkHttps(): boolean {
    if (document.location.hostname === "localhost") {
        return true;
    }

    if (document.location.hostname === "127.0.0.1") {
        return true;
    }

    return 'https:' == document.location.protocol;
}

/**
 * 获得流类型
 *
 * @returns {string}
 */
export function getStreamType(): StreamType {
    let streamType = StreamType.WEBRTC;
    if ($d.isIOS) {
        streamType = StreamType.IOS_WEBRTC;
    } else if ($d.isX5) {
        streamType = StreamType.WECHAT_WEBRTC;
    }

    return streamType;
}

export function changeHttpContent(content: Content, cdnDomain: string): Content {
    if (window.location.protocol === "https:") {
        if (content.thumbs) {
            content.thumbs = replaceCdnDomain(content.thumbs);
        }
        if (content.play.image?.imageUrlPrefix) {
            content.play.image.imageUrlPrefix = replaceCdnDomain(content.play.image.imageUrlPrefix);
        }
        return content;
    }
    if (content.thumbs) {
        content.thumbs = replaceUrl(replaceCdnDomain(content.thumbs));
    }
    if (content.play.image?.imageUrlPrefix) {
        content.play.image.imageUrlPrefix = replaceUrl(replaceCdnDomain(content.play.image.imageUrlPrefix));
    }
    if (content.play.animation?.infoUrl) {
        content.play.animation.infoUrl = replaceUrl(content.play.animation.infoUrl);
    }
    if (content.play.animation?.pageUrlPrefix) {
        content.play.animation.pageUrlPrefix = replaceUrl(content.play.animation.pageUrlPrefix);
    }
    if (content.play.animation?.resourceUri) {
        content.play.animation.resourceUri = replaceUrl(content.play.animation.resourceUri);
    }

    function replaceUrl(url: string) {
        return url.replace("https://", "http://").replace("pubssl", "pub");
    }

    function replaceCdnDomain(input: string) {
        // 修复前的回放课件，需要将后端拼接好的 url 中 livecloud-storage-hz.oss-cn-hangzhou.aliyuncs.com 替换为 cdnDomain
        if (input.startsWith("http://") || input.startsWith("https://")) {
            return input.replace(/^(https?:\/\/)(livecloud-storage-hz\.oss-cn-hangzhou\.aliyuncs\.com)(.*)$/, `$1${cdnDomain}$3`);
        }
        // 修复后的课件（如前端转码的 pdf），后端不拼接域名，由前端拼接
        return `https://${cdnDomain}/${input}`;
    }

    return content;
}

// eslint-disable-next-line
export function checkBrowser(role: string): any {
    const result = {
        ok: false,
        message: "无系统检测结果",
    }

    if (role === "speaker") {
        if (!checkHttps()) {
            result.message = '请使用 HTTPS 地址进入直播课堂。';
            return result;
        }

        if ($d.isIPad && parseInt($d.osVersion as string) < 15) {
            result.message = '您的 iOS 系统版本过低，请升级系统或使用电脑端浏览器';
            return result;
        }

        if ($d.isIE || $d.isFirefox || ($d.isSafari && !$d.isIPad)) {
            result.message = '不支持当前浏览器，请使用微软 Edge、谷歌、360等浏览器。';
            return result;
        }

        if ($d.isWeChat) {
            result.message = '微信浏览器不支持直播，请使用微软 Edge、谷歌、360等浏览器。';
            return result;
        }

        if ($d.engineName === "Chrome" && parseInt($d.engineVersion as string) < 72) {
            result.message = '您的浏览器内核版本过低，请下载使用最新版的微软 Edge、谷歌、360等浏览器。';
            return result;
        }
    } else {
        if ($d.isIE) {
            result.message = '不支持IE浏览器，请使用微软 Edge、谷歌、360等浏览器。';
            return result;
        }

        // Windows 下 PC 版微信内置浏览器内核为 Chrome 53
        // if ($d.isWindows && $d.isWeChat) {
        //     result.message = 'PC 版微信内置浏览器内核版本过低，不支持直播，请使用微软 Edge、谷歌、360等浏览器。';
        //     return result;
        // }

        if ($d.isDesktop && $d.isSafari && $d.browserVersion < 11) {
            result.message = '您的浏览器版本过低，请升级浏览器版本或使用谷歌浏览器。';
            return result;
        }

        if ($d.isAndroid && !$d.isWeChat && !$d.isDingTalk && $d.browserName !== 'QQ Browser') {
            // result.message = '请在手机微信/QQ内置浏览器中打开';
            // return result ;
        }
    }

    result.ok = true;

    return result;
}

type TrafficTagType = "live" | "replay" | "exclude";

/**
 * 生成流量标记
 *
 * @param type
 * @param roomId
 */
export function makeTrafficTag(type: TrafficTagType, roomId: number): string {
    // lct == live cloud traffic tag
    let tag = `${type}.${roomId}`;

    let sign = 0;
    for (let i=0; i< tag.length; i++) {
        sign += tag.charCodeAt(i) % 16;
    }

    tag += '.s.' + sign.toString(16);

    return '_lct=' + tag;
}

/**
 * 将对象序列为字符串
 *
 * @param obj
 */
export function stringify(obj: unknown): string {
    if (obj instanceof Error) {
        const error = errorToJSON(obj) as {stack : string | undefined};
        if (error.stack) {
            delete error.stack;
        }

        return JSON.stringify(error);
    }

    return JSON.stringify(obj);
}

export function safeBase64Decode(safe: string): string {
    return safe.replace(/[-_.]/g, (m):string => {
        if (m === '-') {
            return '+';
        } else if (m === '_') {
            return '/';
        } else if (m === '.') {
            return '=';
        }
        return m;
    })
}

export function getUrlQueryString(name: string, url = window.location.href): string | null {
    name = name = name.replace(/[[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
        results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
}


export function buildImgSrc(uri: string, domain: string): string {
    if (uri.startsWith("data:image")) {
        return uri;
    }

    if (uri.startsWith("{")) {
        const parsed = JSON.parse(uri);
        return "//" + domain + "/" + parsed.uri;
    }

    return "//" + domain + "/" + uri;
}

export function convertEmojiToHtml(text: string): string {
    const emoji = Emojis.getEmoji(text);
    if (!emoji) {
        return escape(text);
    }

    return `<img height="20px" width="20px" src="${emoji.src}" />`;
}

export function formatMoney(money: number): string {
    if (money < 10) {
        return `0.<small>0${money}</small>`;
    }
    if (money < 100) {
        return `0.<small>${money}</small>`;
    }

    const strMoney = '' + money;
    const s1 = strMoney.substr(0, strMoney.length-2);
    const s2 = strMoney.substr(strMoney.length-2);

    return `${s1}.<small>${s2}</small>`;
}


export function showWatermark(txt: string, h5 = false): void {
    if (!txt) {
        return;
    }

    gwm.creation({
        txt: txt.replace(/\n/g, "<br>"),
        mode: 'element',
        width: h5 ? 180 : 300,
        height: h5 ? 120 : 200,
        // color: '#d0d0d2',
        css: {
            'z-index': 99999,
            'pointer-events': 'none',
        },
        alpha: 0.1,
    });
}
